// ====================================================================
//  EYEWALL — WATCHERS.EXE  (Ambulation Zone / The Annex)
//  Derived from THE EYE crossed with the all-eyes-reserved / lookatem
//  motif: a panel of MANY small but believable human eyes whose pupils
//  all track the cursor at once.
//
//  Each eye is a lightweight inline SVG (NOT a canvas): the iris is a
//  radialGradient (light inner ring -> dark rim) overlaid with fine
//  radial fiber striations and a darker limbal ring; a black pupil with a
//  crisp white specular catchlight offset toward a shared top-left light;
//  a softly shaded wet sclera (not flat white) carrying one or two faint
//  red veins; and upper/lower almond eyelid arcs with a crease shadow and
//  a few lashes. The upper lid sweeps closed via a pure-CSS keyframe with
//  staggered per-eye delays for natural, desynchronised blinking.
//
//  PERFORMANCE: zero canvases, zero per-eye timers. ONE shared document
//  mousemove listener (rafThrottle from the shared util) translates every
//  iris+pupil group toward the pointer via a CSS transform, clamped inside
//  the sclera. The listener is removed (and the rAF wrapper cancelled) on
//  unmount. Blinks are CSS-only.
//
//  Mounts inside a WindowsCard with programText WATCHERS.EXE.
//  Functional component, normal JSX.
// ====================================================================

import React, { useEffect, useMemo, useRef } from 'react';
import { rafThrottle, prefersReducedMotion } from '../_shared/sketchFrame';
import eyeData from './eyes.json';
import './eyewall.css';

// SVG geometry constants (viewBox 0 0 100 80). The iris is allowed to
// travel this far from eye-center before being clamped, so the pupil never
// slides off the sclera.
const EYE_CX = 50;
const EYE_CY = 40;
const TRAVEL_X = 11; // max horizontal iris travel (svg units)
const TRAVEL_Y = 7;  // max vertical iris travel (svg units)

// One reusable realistic eye as inline SVG. `idx` namespaces the SVG defs
// (gradients / clip paths must have unique ids per instance).
function Eye({ idx, hue, sat, light, scale, blinkDelay, blinkDur, reduced }) {
    const irisGrad = `ew-iris-${idx}`;
    const scleraGrad = `ew-sclera-${idx}`;
    const lidClip = `ew-lid-${idx}`;

    // base / rim iris colors from the per-eye hue, derived once.
    const irisInner = `hsl(${hue} ${sat}% ${light + 16}%)`;
    const irisMid = `hsl(${hue} ${sat}% ${light}%)`;
    const irisRim = `hsl(${hue} ${Math.min(sat + 10, 70)}% ${Math.max(light - 16, 10)}%)`;

    // pre-compute fine radial fiber striations (cheap static lines).
    const fibers = useMemo(() => {
        const out = [];
        const N = 26;
        for (let i = 0; i < N; i++) {
            const a = (i / N) * Math.PI * 2 + (i % 3) * 0.04;
            const ri = 4.5; // inner radius (just outside pupil)
            const ro = 9.4; // outer radius (iris rim)
            out.push({
                x1: Math.cos(a) * ri,
                y1: Math.sin(a) * ri,
                x2: Math.cos(a) * ro,
                y2: Math.sin(a) * ro,
                // alternate light/dark fibers for the fibrous look
                light: i % 2 === 0 ? light + 22 : Math.max(light - 12, 8),
                w: 0.18 + (i % 3) * 0.05
            });
        }
        return out;
    }, [light]);

    const lidStyle = reduced
        ? undefined
        : { animationDuration: `${blinkDur}s`, animationDelay: `${blinkDelay}s` };

    return (
        <svg
            className="eyewall-eye"
            viewBox="0 0 100 80"
            style={{ '--eye-scale': scale }}
            aria-hidden="true"
        >
            <defs>
                {/* sclera: soft wet shading, brighter toward top-left light */}
                <radialGradient id={scleraGrad} cx="42%" cy="34%" r="72%">
                    <stop offset="0%" stopColor="#fcf8f3" />
                    <stop offset="62%" stopColor="#eadfd2" />
                    <stop offset="100%" stopColor="#c7b6a2" />
                </radialGradient>

                {/* iris: lighter inner ring -> darker rim */}
                <radialGradient id={irisGrad} cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={irisInner} />
                    <stop offset="55%" stopColor={irisMid} />
                    <stop offset="100%" stopColor={irisRim} />
                </radialGradient>

                {/* almond opening — clips the eyeball contents to the lids */}
                <clipPath id={lidClip}>
                    <path d="M 14 40
                             C 28 22, 72 22, 86 40
                             C 72 58, 28 58, 14 40 Z" />
                </clipPath>
            </defs>

            {/* ---- eyeball contents, clipped to the almond ---- */}
            <g clipPath={`url(#${lidClip})`}>
                {/* shaded sclera */}
                <rect x="0" y="0" width="100" height="80" fill={`url(#${scleraGrad})`} />

                {/* inner-corner curvature shadow + warm caruncle hint */}
                <ellipse cx="50" cy="26" rx="40" ry="14" fill="#7a6b60" opacity="0.10" />
                <ellipse cx="17" cy="42" rx="5" ry="3.4" fill="#d49696" opacity="0.45" />

                {/* faint branching red veins */}
                <g stroke="#be3834" strokeWidth="0.5" fill="none" opacity="0.5" strokeLinecap="round">
                    <path d="M 16 38 C 22 36, 26 39, 30 37 M 28 38 C 30 40, 33 39, 35 41" />
                    <path d="M 84 44 C 78 45, 75 42, 71 44" />
                </g>

                {/* ---- iris + pupil group : moved by the shared listener ---- */}
                <g className="eyewall-iris" data-eye>
                    <g transform={`translate(${EYE_CX} ${EYE_CY})`}>
                        {/* iris base gradient disc */}
                        <circle r="9.6" fill={`url(#${irisGrad})`} />

                        {/* fine radial fiber striations */}
                        <g strokeLinecap="round">
                            {fibers.map((f, i) => (
                                <line
                                    key={i}
                                    x1={f.x1.toFixed(2)} y1={f.y1.toFixed(2)}
                                    x2={f.x2.toFixed(2)} y2={f.y2.toFixed(2)}
                                    stroke={`hsl(${hue} ${sat}% ${f.light}%)`}
                                    strokeWidth={f.w}
                                    opacity="0.6"
                                />
                            ))}
                        </g>

                        {/* lighter collarette ring just outside the pupil */}
                        <circle r="5" fill="none" stroke={irisInner} strokeWidth="0.5" opacity="0.6" />
                        {/* darker limbal ring at the iris edge */}
                        <circle r="9.5" fill="none" stroke="#161b22" strokeWidth="0.9" opacity="0.85" />

                        {/* pupil — black with a soft halo for depth */}
                        <circle r="4.6" fill="#000" opacity="0.35" />
                        <circle r="3.9" fill="#000" />

                        {/* crisp specular catchlight (top-left) + faint secondary */}
                        <circle cx="-2.6" cy="-3" r="1.5" fill="#ffffff" opacity="0.95" />
                        <circle cx="2" cy="2.4" r="0.8" fill="#ffffff" opacity="0.5" />
                    </g>
                </g>

                {/* wet glare sweeping across the top of the eyeball */}
                <path d="M 18 32 C 34 26, 66 26, 82 32 C 66 30, 34 30, 18 32 Z"
                      fill="#ffffff" opacity="0.22" />
            </g>

            {/* ---- upper-lid blink mask : skin-toned, sweeps down via CSS ---- */}
            <g clipPath={`url(#${lidClip})`}>
                <rect
                    className="eyewall-lid"
                    x="10" y="18" width="80" height="24"
                    fill="#1d1714"
                    style={lidStyle}
                />
            </g>

            {/* ---- almond rim ink outline (upper + lower lid arcs) ---- */}
            <path
                d="M 14 40 C 28 22, 72 22, 86 40 C 72 58, 28 58, 14 40 Z"
                fill="none" stroke="#120d0b" strokeWidth="1.4"
            />
            {/* soft crease shadow above the upper lid */}
            <path
                d="M 18 33 C 32 21, 68 21, 82 33"
                fill="none" stroke="#3a2a24" strokeWidth="2" opacity="0.4"
            />

            {/* a few upper lashes, curling up and out */}
            <g stroke="#0e0907" strokeWidth="1" fill="none" strokeLinecap="round">
                <path d="M 30 26 q -2 -4, -4 -6" />
                <path d="M 42 23 q -1 -4, -2 -7" />
                <path d="M 58 23 q 1 -4, 2 -7" />
                <path d="M 70 26 q 2 -4, 4 -6" />
            </g>
            {/* one lower lash hint */}
            <path d="M 50 57 l 0 5" stroke="#0e0907" strokeWidth="0.7" />
        </svg>
    );
}

function EyeWall() {
    const wrapRef = useRef(null);
    const reduced = useMemo(() => prefersReducedMotion(), []);

    useEffect(() => {
        const root = wrapRef.current;
        if (!root) return undefined;

        // Collect every iris+pupil group ONCE; the listener just retargets them.
        const irises = Array.prototype.slice.call(
            root.querySelectorAll('.eyewall-iris')
        );
        if (!irises.length) return undefined;

        // Single shared, rAF-coalesced pointer handler. Each iris translates
        // toward the cursor, normalised by ITS OWN on-screen position so all
        // eyes converge on the pointer, then clamped inside the sclera.
        const onMove = rafThrottle((e) => {
            const px = e.clientX;
            const py = e.clientY;
            for (let i = 0; i < irises.length; i++) {
                const el = irises[i];
                const r = el.getBoundingClientRect();
                const cx = r.left + r.width / 2;
                const cy = r.top + r.height / 2;
                // direction to pointer, in -1..1 of a comfortable reach radius
                const dx = (px - cx) / 90;
                const dy = (py - cy) / 90;
                const len = Math.hypot(dx, dy) || 1;
                // clamp magnitude to 1 so far-away pointers don't overshoot
                const m = Math.min(len, 1) / len;
                // svg user units; scale by element width so translate matches
                const ux = Math.max(-1, Math.min(1, dx * m)) * TRAVEL_X;
                const uy = Math.max(-1, Math.min(1, dy * m)) * TRAVEL_Y;
                el.style.transform = `translate(${ux.toFixed(2)}px, ${uy.toFixed(2)}px)`;
            }
        });

        document.addEventListener('mousemove', onMove, { passive: true });
        return () => {
            document.removeEventListener('mousemove', onMove);
            if (onMove && onMove.cancel) onMove.cancel();
        };
    }, []);

    const eyes = eyeData.eyes || [];

    return (
        <div ref={wrapRef} className="eyewall-wrap">
            <div className="eyewall-grid">
                {eyes.map((e, i) => (
                    <Eye
                        key={i}
                        idx={i}
                        hue={e.hue}
                        sat={e.sat}
                        light={e.light}
                        scale={e.scale}
                        blinkDelay={e.blinkDelay}
                        blinkDur={e.blinkDur}
                        reduced={reduced}
                    />
                ))}
            </div>
        </div>
    );
}

export default EyeWall;
